import { Modal, Row, Col } from "antd";
import { Box, Flex, Textarea, Text } from "@chakra-ui/react";
import OutlineButton from "components/Button/OutlineButton";
import PrimaryButton from "components/Button/PrimaryButton";
import Field from "components/Input/Field";
import { useForm } from "react-hook-form";
import { showSuccess, showErrorAxios } from "infra/helpers/alerta";
import { useEffect } from "react";
import Anexos from "components/Anexos";
import ButtonAnexarArquivos from "components/Button/ButtonAnexarArquivos";
import useSaveEquipamento from "features/equipamento/hooks/useSaveEquipamento";
import { EquipamentoForm } from "features/equipamento/typings/index";
import SelectMarcaEquipamento from "components/Input/SelectMarcaEquipamento";
import { useEquipamentoDetalhe } from "features/equipamento/hooks/useEquipamentoDetalhe";
import { useLoading } from "components/GlobalLoading";
import InputMoney from "components/Input/InputMoney";
import SelectTipoLocalEquipamento from "components/Input/SelectTipoLocalEquipamento";
import SelectAreaComum from "components/Input/SelectAreaComum";
import { TipoLocalEquipamentoEnum } from "features/equipamento/typings";

interface ModalCadastroEquipamentoProps {
  visible: boolean;
  onClose: () => any;
  onSaved: (id?: string, nome?: string) => any;
  refetchList?: () => any;
  id?: string;
}

const defaultValuesFormEquipamento: Partial<EquipamentoForm> = {
  arquivos: [],
  nome: "",
  local: "",
  marcaId: "",
  observacao: "",
  anoFabricacao: "",
  dataCompra: "",
  dataInstalacao: "",
  id: undefined,
};

export default function ModalCadastrarEquipamento({
  onSaved,
  visible,
  onClose,
  refetchList,
  id,
}: ModalCadastroEquipamentoProps) {
  const { data, isLoading, isFetching } = useEquipamentoDetalhe(id);
  const { handleSubmit, control, setValue, watch, reset } =
    useForm<EquipamentoForm>({
      defaultValues: defaultValuesFormEquipamento,
    });

  const { mutate: handleSave, isLoading: isLoadingSave } = useSaveEquipamento();

  const onSubmit = async (form: EquipamentoForm) => {
    handleSave(form, {
      onSuccess: ({ id }) => {
        setTimeout(() => {
          showSuccess().then(() => onSaved(id, form.nome));
        }, 250);
        refetchList?.();
      },
      onError: (err) => {
        showErrorAxios(err);
      },
    });
  };

  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data, reset]);

  useEffect(() => {
    if (!visible) {
      reset(defaultValuesFormEquipamento);
    }
  }, [reset, visible]);

  useLoading(isLoading || isFetching);

  const form = watch();
  return (
    <Modal
      destroyOnClose
      width={"600px"}
      onCancel={onClose}
      centered
      title="Cadastrar novo patrimônio"
      visible={visible}
      footer={
        <Flex justifyContent="end" gap={4}>
          <OutlineButton onClick={onClose}>Cancelar</OutlineButton>
          <PrimaryButton
            isLoading={isLoadingSave}
            onClick={() => handleSubmit(onSubmit)()}
          >
            Salvar
          </PrimaryButton>
        </Flex>
      }
    >
      <form onSubmit={(e) => handleSubmit(onSubmit)(e)}>
        <Box as={Row} gutter={16}>
          <Box as={Col} mb={4} md={12}>
            <Field
              label="Nome do patrimônio"
              name="nome"
              rules={{ required: "Campo obrigatório" }}
              control={control}
            />
          </Box>
          <Box as={Col} mb={4} md={12}>
            <Field
              label="Marca"
              name="marcaId"
              control={control}
              render={({ field }) => <SelectMarcaEquipamento {...field} />}
            />
          </Box>
        </Box>

        <Box as={Row} gutter={16}>
          <Box as={Col} mb={4} md={12}>
            <Field
              label="Local do patrimônio"
              name="tipoLocalEquipamentoId"
              rules={{ required: "Campo obrigatório" }}
              control={control}
              render={({ field }) => <SelectTipoLocalEquipamento {...field} />}
            />
          </Box>
          {form.tipoLocalEquipamentoId ===
          TipoLocalEquipamentoEnum.AreaComum ? (
            <Box as={Col} mb={4} md={12}>
              <Field
                shouldUnregister
                label="Área comum"
                name="areaComumId"
                rules={{ required: "Campo obrigatório" }}
                control={control}
                render={({ field }) => (
                  <SelectAreaComum
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Box>
          ) : null}
          {form.tipoLocalEquipamentoId === TipoLocalEquipamentoEnum.Outros ? (
            <Box as={Col} mb={4} md={12}>
              <Field
                shouldUnregister
                label="Local do patrimônio"
                name="local"
                rules={{ required: "Campo obrigatório" }}
                control={control}
              />
            </Box>
          ) : null}
        </Box>

        <Box as={Row} gutter={16}>
          <Box as={Col} mb={4} md={8}>
            <Field
              label="Ano de fabricação"
              name="anoFabricacao"
              control={control}
              render={({ field }) => (
                <InputMoney
                  {...field}
                  decimalScale={0}
                  decimalsLimit={0}
                  disableGroupSeparators
                  allowDecimals={false}
                  maxLength={4}
                  placeholder="XXXX"
                  style={{
                    textAlign: "left",
                  }}
                />
              )}
            />
          </Box>

          <Box as={Col} mb={4} md={8}>
            <Field
              label="Data de compra"
              name="dataCompra"
              inputProps={{ type: "date" }}
              control={control}
            />
          </Box>

          <Box as={Col} mb={4} md={8}>
            <Field
              label="Data de instalação"
              name="dataInstalacao"
              rules={{ required: "Campo obrigatório" }}
              inputProps={{ type: "date" }}
              control={control}
            />
          </Box>
        </Box>

        <Box mb={4}>
          <Field
            label="Observação"
            name="observacao"
            inputProps={{ type: "text" }}
            control={control}
            render={({ field, isInvalid }) => (
              <Textarea
                isInvalid={isInvalid}
                className="chakra-input"
                rows={4}
                resize="none"
                placeholder="Digite..."
                {...field}
              />
            )}
          />
        </Box>

        <Box>
          <Text mb="2px" fontSize="13px">
            Fotos
          </Text>

          <Flex flex={1}>
            <ButtonAnexarArquivos
              maxFileSizeMb={20}
              currentFilesLength={form.arquivos?.length || 0}
              variant="ghost"
              onAnexar={(files) => {
                if (files) {
                  const currentAnexos = form.arquivos;

                  setValue(
                    "arquivos",
                    currentAnexos ? [...currentAnexos, ...files] : files
                  );
                }
              }}
            />
          </Flex>
          <Flex
            gap="16px"
            mt="auto"
            h="fit-content"
            px={4}
            py={2}
            flexDir="column"
          >
            {form.arquivos?.length ? (
              <Anexos
                numColumns={2}
                setValue={(anexos) =>
                  setValue(
                    "arquivos",
                    anexos?.map((anexo) =>
                      Object.assign(anexo, { nome: anexo.name })
                    )
                  )
                }
                anexos={
                  form.arquivos?.map((file) =>
                    file instanceof File ? file : { ...file, name: file.nome }
                  ) || undefined
                }
              />
            ) : null}
          </Flex>
        </Box>
      </form>
    </Modal>
  );
}
